import React, { useState, useEffect, useRef, useCallback } from 'react';
import styles from './SentimentModule.module.css';
import { Project } from '../../types';
import { Button } from '../common/Button/Button';
import { CoostoSettings } from './CoostoSettings';
import { SentimentAnalysisPanel } from './SentimentAnalysisPanel';
import { getApiBaseUrl } from '../../services/api';
import {
  XLogoIcon,
  CloudIcon,
  NewspaperIcon,
  CameraIcon,
  GlobeIcon,
  WarningTriangleIcon,
  ThumbsUpIcon,
  ThumbsDownIcon,
  MinusCircleIcon
} from '../common/Icons/ActionIcons';

// SVG icoon voor publicaties (document)
const PublicatieIcon: React.FC<{ size?: number }> = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
    <polyline points="10 9 9 9 8 9" />
  </svg>
);

/**
 * Detecteer of een URL van een officiële overheidspublicatie is
 */
function isOfficielPublicatie(url: string, sourceType: string): boolean {
  if (!url) return false;
  const lc = url.toLowerCase();
  return lc.includes('officielebekendmakingen.nl') ||
    lc.includes('overheid.nl') ||
    lc.includes('gemeenteblad') ||
    lc.includes('staatscourant') ||
    lc.includes('zoek.overheid.nl');
}

/**
 * Detecteer of een bericht een liveblog/verzamelpagina is
 */
function isLiveblogOrVerzamelpagina(msg: CoostoMessage): boolean {
  const url = (msg.url || '').toLowerCase();
  const text = (msg.text || msg.snippet || '').toLowerCase();

  // Bekende liveblog/verzamelpagina patronen
  if (url.includes('/liveblog') || url.includes('/live/') || url.includes('/laatste-nieuws')) return true;
  if (url.includes('nu.nl') && (url.includes('/algemeen/') || url.includes('/binnenland/'))) {
    // Nu.nl verzamelpagina's hebben vaak zeer korte snippets of generieke titels
    if (text.length < 60) return true;
  }

  // Generieke indicator: als de tekst lijkt op een dagelijks nieuwsoverzicht
  const liveblogKeywords = ['liveblog', 'dit is wat je moet weten', 'het laatste nieuws', 'overzicht van de dag', 'dit gebeurde er'];
  if (liveblogKeywords.some(kw => text.includes(kw))) return true;

  return false;
}

/**
 * Dedupliceer berichten op basis van URL en tekst-gelijkenis
 */
function deduplicateMessages(messages: CoostoMessage[]): CoostoMessage[] {
  const seen = new Map<string, CoostoMessage>();

  for (const msg of messages) {
    // Primaire dedup: zelfde URL
    const url = (msg.url || '').split('?')[0].split('#')[0].toLowerCase().replace(/\/+$/, '');
    if (url && seen.has(url)) continue;

    // Secundaire dedup: zelfde tekst (genormaliseerd)
    const textKey = (msg.text || msg.snippet || '').toLowerCase().replace(/\s+/g, ' ').trim().substring(0, 200);
    if (textKey.length > 30 && seen.has(`txt:${textKey}`)) continue;

    if (url) seen.set(url, msg);
    if (textKey.length > 30) seen.set(`txt:${textKey}`, msg);
    seen.set(`id:${msg.id}`, msg);
  }

  // Return unieke berichten (alleen de id-keyed entries)
  const result: CoostoMessage[] = [];
  seen.forEach((msg, key) => {
    if (key.startsWith('id:')) result.push(msg);
  });
  return result;
}

interface SentimentModuleProps {
  project: Project;
  sentimentData?: any[];
}

interface CoostoMessage {
  sourceType: string;
  id: string;
  label: string;
  url: string;
  text: string;
  snippet: string;
  author: {
    name: string;
    userName: string;
    displayName: string;
    avatar: string;
    id: string;
    followersAmount: number;
  };
  media: Array<{
    url: string;
    type: string;
    preview: {
      url: string;
      type: string;
    };
  }>;
  timestamp: number;
  sentiment: 'positive' | 'negative' | 'neutral';
  language: string;
  reach: number;
  isRetweet: boolean;
  isRepost: boolean;
}

interface SentimentData {
  title: string;
  lastUpdated: string;
  coostoProjectId: number;
  searchQuery: string;
  messages: CoostoMessage[];
  totalMessages: number;
  sentimentDistribution: {
    positive: number;
    negative: number;
    neutral: number;
  };
}

type SourceCategory = 'all' | 'media' | 'social' | 'publicatie';

/**
 * Bepaal de broncategorie van een bericht
 */
function getSourceCategory(sourceType: string, url: string): SourceCategory {
  if (isOfficielPublicatie(url, sourceType)) return 'publicatie';
  const st = sourceType.toLowerCase();
  if (st === 'news') return 'media';
  if (['twitter', 'facebook', 'instagram', 'bluesky'].includes(st)) return 'social';
  return 'media'; // default
}

/**
 * Bepaal betrouwbaarheidsniveau van een bron
 * 3 = hoog (officiële publicaties, gevestigde media)
 * 2 = gemiddeld (regionale/lokale media, bekende accounts)
 * 1 = laag (individuele social accounts)
 */
function getSourceReliability(sourceType: string, url: string, author?: { followersAmount: number }): { level: number; label: string; color: string } {
  if (isOfficielPublicatie(url, sourceType)) {
    return { level: 3, label: 'Hoog', color: '#4CAF50' };
  }
  const st = sourceType.toLowerCase();
  if (st === 'news') {
    // Gevestigde media
    const lc = (url || '').toLowerCase();
    const majorMedia = ['nos.nl', 'ad.nl', 'volkskrant.nl', 'nrc.nl', 'telegraaf.nl', 'trouw.nl', 'rtv', 'omroep', 'bnr.nl', 'nu.nl'];
    if (majorMedia.some(m => lc.includes(m))) {
      return { level: 3, label: 'Hoog', color: '#4CAF50' };
    }
    return { level: 2, label: 'Gemiddeld', color: '#FF9800' };
  }
  // Social media
  if (author && author.followersAmount > 5000) {
    return { level: 2, label: 'Gemiddeld', color: '#FF9800' };
  }
  return { level: 1, label: 'Laag', color: '#9DA6A8' };
}

export const SentimentModule: React.FC<SentimentModuleProps> = ({ project }) => {
  const [sentimentData, setSentimentData] = useState<SentimentData | null>(null);
  const [sentimentAnalysis, setSentimentAnalysis] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [sentimentPosition, setSentimentPosition] = useState(50);
  const [selectedFilter, setSelectedFilter] = useState<'all' | 'positive' | 'negative' | 'neutral'>('all');
  const [selectedSourceFilter, setSelectedSourceFilter] = useState<SourceCategory>('all');
  const [activeTab, setActiveTab] = useState<'overview' | 'settings'>('overview');
  const [currentPage, setCurrentPage] = useState(1);
  const [activeVoiceCategory, setActiveVoiceCategory] = useState<string | null>(null);
  const [showPeriodComparison, setShowPeriodComparison] = useState(false);
  const [periodA, setPeriodA] = useState<{ from: string; to: string }>({
    from: new Date(new Date().setMonth(new Date().getMonth() - 2)).toISOString().split('T')[0],
    to: new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split('T')[0]
  });
  const [periodB, setPeriodB] = useState<{ from: string; to: string }>({
    from: new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split('T')[0],
    to: new Date().toISOString().split('T')[0]
  });
  const messagesListRef = useRef<HTMLDivElement>(null);
  const messagesPerPage = 20;

  useEffect(() => {
    fetchSentiments();
  }, [project.id]);

  const fetchSentiments = async () => {
    setLoading(true);
    try {
      const API_BASE = getApiBaseUrl();
      const response = await fetch(`${API_BASE}/projects/${project.id}/sentiment`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();

      if (data.success && data.sentiment) {
        setSentimentData(data.sentiment);
        if (data.sentimentAnalysis) {
          setSentimentAnalysis(data.sentimentAnalysis);
        }

        // Calculate sentiment position based on distribution
        const dist = data.sentiment.sentimentDistribution;
        const total = data.sentiment.messages?.length || 0;

        if (dist && total > 0) {
          // Calculate weighted position (0-100 scale)
          const positiveWeight = (dist.positive / total) * 100;
          const negativeWeight = (dist.negative / total) * 100;
          const neutralWeight = (dist.neutral / total) * 50;

          // Position is weighted average, with negative pulling left, positive pulling right
          const position = 50 + (positiveWeight * 0.5) - (negativeWeight * 0.5);
          setSentimentPosition(Math.max(0, Math.min(100, position)));
        } else {
          setSentimentPosition(50); // Default to neutral if no data
        }
      }
    } catch (error) {
      console.error('Failed to fetch sentiments:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSentimentLabel = () => {
    if (sentimentPosition < 20) return { text: 'Zeer Negatief', color: '#DC3545' };
    if (sentimentPosition < 40) return { text: 'Negatief', color: '#FF6B35' };
    if (sentimentPosition < 60) return { text: 'Neutraal', color: '#FFC107' };
    if (sentimentPosition < 80) return { text: 'Positief', color: '#8BC34A' };
    return { text: 'Zeer Positief', color: '#00C853' };
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('nl-NL').format(num);
  };

  const getCleanedMessages = useCallback(() => {
    if (!sentimentData?.messages) return [];
    // 1. Filter liveblogs en verzamelpagina's
    const withoutLiveblogs = sentimentData.messages.filter(msg => !isLiveblogOrVerzamelpagina(msg));
    // 2. Dedupliceer
    return deduplicateMessages(withoutLiveblogs);
  }, [sentimentData?.messages]);

  const getFilteredMessages = () => {
    let cleaned = getCleanedMessages();
    if (selectedFilter !== 'all') {
      cleaned = cleaned.filter(msg => msg.sentiment === selectedFilter);
    }
    if (selectedSourceFilter !== 'all') {
      cleaned = cleaned.filter(msg => getSourceCategory(msg.sourceType, msg.url) === selectedSourceFilter);
    }
    return cleaned;
  };

  const getPaginatedMessages = () => {
    const filtered = getFilteredMessages();
    const startIndex = (currentPage - 1) * messagesPerPage;
    const endIndex = startIndex + messagesPerPage;
    return filtered.slice(startIndex, endIndex);
  };

  const getTotalPages = () => {
    const filtered = getFilteredMessages();
    return Math.ceil(filtered.length / messagesPerPage);
  };

  // Reset to page 1 when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedFilter, selectedSourceFilter]);

  const scrollToMessages = useCallback(() => {
    setTimeout(() => {
      messagesListRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  }, []);

  const handleFilterAndScroll = useCallback((filter: 'all' | 'positive' | 'negative' | 'neutral') => {
    setSelectedFilter(filter);
    setActiveVoiceCategory(null);
    scrollToMessages();
  }, [scrollToMessages]);

  const handleVoiceCategoryClick = useCallback((categoryKey: string, categoryLabel: string) => {
    // Bepaal het sentiment-filter op basis van de voice category group
    const negativeKeys = ['constructivelyCritical', 'emotionalPrincipled', 'factualInforming', 'supportive'];
    const isNegative = negativeKeys.includes(categoryKey);
    setSelectedFilter(isNegative ? 'negative' : 'positive');
    setActiveVoiceCategory(categoryKey);
    scrollToMessages();
  }, [scrollToMessages]);

  const analyzeMessages = async () => {
    setAnalyzing(true);
    try {
      const API_BASE = getApiBaseUrl();
      const response = await fetch(`${API_BASE}/projects/${project.id}/sentiment/analyze`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();

      if (data.success && data.sentimentAnalysis) {
        setSentimentAnalysis(data.sentimentAnalysis);
      }
    } catch (error) {
      console.error('Failed to analyze sentiments:', error);
    } finally {
      setAnalyzing(false);
    }
  };

  const getPeriodComparison = useCallback(() => {
    if (!sentimentData?.messages) return null;
    const cleaned = getCleanedMessages();

    const filterByPeriod = (msgs: CoostoMessage[], from: string, to: string) => {
      const fromTs = new Date(from).getTime() / 1000;
      const toTs = new Date(to).getTime() / 1000 + 86400; // include end day
      return msgs.filter(m => m.timestamp >= fromTs && m.timestamp < toTs);
    };

    const analyze = (msgs: CoostoMessage[]) => {
      const media = msgs.filter(m => getSourceCategory(m.sourceType, m.url) === 'media');
      const social = msgs.filter(m => getSourceCategory(m.sourceType, m.url) === 'social');

      const dist = (subset: CoostoMessage[]) => ({
        positive: subset.filter(m => m.sentiment === 'positive').length,
        negative: subset.filter(m => m.sentiment === 'negative').length,
        neutral: subset.filter(m => m.sentiment === 'neutral').length,
        total: subset.length,
      });

      return { all: dist(msgs), media: dist(media), social: dist(social) };
    };

    const msgsA = filterByPeriod(cleaned, periodA.from, periodA.to);
    const msgsB = filterByPeriod(cleaned, periodB.from, periodB.to);

    return { a: analyze(msgsA), b: analyze(msgsB) };
  }, [sentimentData?.messages, periodA, periodB, getCleanedMessages]);

  const getSourceDisplay = (sourceType: string, url: string): { icon: React.ReactNode; label: string } => {
    // Check voor officiële publicatie EERST (overschrijft 'news')
    if (isOfficielPublicatie(url, sourceType)) {
      return { icon: <PublicatieIcon size={16} />, label: 'Publicatie' };
    }

    switch (sourceType.toLowerCase()) {
      case 'twitter':
        return { icon: <XLogoIcon size={16} />, label: 'Twitter' };
      case 'bluesky':
        return { icon: <CloudIcon size={16} />, label: 'Bluesky' };
      case 'news':
        return { icon: <NewspaperIcon size={16} />, label: 'News' };
      case 'facebook':
        return { icon: <span style={{ fontWeight: 700, fontSize: '14px' }}>f</span>, label: 'Facebook' };
      case 'instagram':
        return { icon: <CameraIcon size={16} />, label: 'Instagram' };
      default:
        return { icon: <GlobeIcon size={16} />, label: sourceType };
    }
  };

  if (loading) {
    return (
      <div className={styles.loading}>
        <div className={styles.loadingSpinner}></div>
        <p>Sentimentdata laden...</p>
      </div>
    );
  }

  if (!sentimentData) {
    return (
      <div className={styles.empty}>
        <p>Geen sentimentdata beschikbaar voor dit project.</p>
      </div>
    );
  }

  const sentiment = getSentimentLabel();
  const filteredMessages = getFilteredMessages();
  const paginatedMessages = getPaginatedMessages();
  const totalPages = getTotalPages();

  return (
    <div className={styles.sentimentModule}>
      <div className={styles.header}>
        <div>
          <h1>Sentimentanalyse</h1>
          {sentimentData?.title && (
            <h2 className={styles.subtitle}>{sentimentData.title}</h2>
          )}
        </div>
        <div className={styles.tabNav}>
          {activeTab === 'overview' && sentimentData?.messages && sentimentData.messages.length > 0 && (
            <Button
              variant="secondary"
              size="small"
              onClick={analyzeMessages}
              disabled={analyzing}
            >
              {analyzing ? 'Analyseren...' : 'Heranalyseer berichten'}
            </Button>
          )}
          <button
            className={`${styles.tabButton} ${activeTab === 'overview' ? styles.activeTab : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            Overview
          </button>
          <button
            className={`${styles.tabButton} ${activeTab === 'settings' ? styles.activeTab : ''}`}
            onClick={() => setActiveTab('settings')}
          >
            Instellingen
          </button>
        </div>
      </div>

      {activeTab === 'settings' ? (
        <CoostoSettings
          projectId={project.id}
          onSyncComplete={() => {
            fetchSentiments();
            setActiveTab('overview');
          }}
        />
      ) : sentimentData ? (
        <>
          <div className={styles.lastUpdated}>
            Laatst bijgewerkt: {new Date(sentimentData.lastUpdated).toLocaleDateString('nl-NL', {
              day: 'numeric',
              month: 'long',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}
          </div>

      {/* AI Sentiment Analysis Panel */}
      {sentimentAnalysis && (
        <SentimentAnalysisPanel data={sentimentAnalysis} onVoiceCategoryClick={handleVoiceCategoryClick} />
      )}

      {/* Alert Card voor negatieve sentiments */}
      {sentimentData.sentimentDistribution.negative > 5 && (
        <div className={`${styles.card} ${styles.alertCard}`}>
          <div className={styles.alertIcon}><WarningTriangleIcon size={20} /></div>
          <div className={styles.alertContent}>
            <div className={styles.alertTitle}>Aandacht vereist</div>
            <div className={styles.alertMessage}>
              {sentimentData.sentimentDistribution.negative} negatieve berichten gedetecteerd
            </div>
          </div>
          <div className={styles.alertActions}>
            <Button
              variant="secondary"
              size="small"
              onClick={() => handleFilterAndScroll('negative')}
            >
              Toon negatieve berichten
            </Button>
          </div>
        </div>
      )}

      {/* Sentiment Meter */}
      <div className={styles.card}>
        <h2>Algemeen Sentiment</h2>
        <div className={styles.sentimentInfo}>
          <span className={styles.messagesCount}>
            {formatNumber(filteredMessages.length)} van {formatNumber(sentimentData.totalMessages)} berichten geladen
          </span>
        </div>
        <div className={styles.sentimentMeter}>
          <div className={styles.meterTrack}>
            <div
              className={styles.meterIndicator}
              style={{
                left: `${sentimentPosition}%`,
                backgroundColor: sentiment.color
              }}
            />
          </div>
          <div className={styles.meterLabels}>
            <span>Zeer Negatief</span>
            <span>Neutraal</span>
            <span>Zeer Positief</span>
          </div>
        </div>
        <div className={styles.sentimentValue} style={{ color: sentiment.color }}>
          {sentiment.text}
        </div>
      </div>

      {/* Sentiment Distributie */}
      <div className={styles.card}>
        <h3>Sentiment Distributie</h3>
        <div className={styles.distribution}>
          <div className={`${styles.distributionItem} ${styles.distributionClickable}`} onClick={() => handleFilterAndScroll('positive')}>
            <span className={styles.distributionLabel}>Positief</span>
            <div className={styles.distributionBar}>
              <div
                className={styles.distributionFill}
                style={{
                  width: `${(sentimentData.sentimentDistribution.positive / Math.max(1, sentimentData.messages.length)) * 100}%`,
                  backgroundColor: '#8BC34A'
                }}
              />
            </div>
            <span className={styles.distributionValue}>
              {sentimentData.sentimentDistribution.positive}
            </span>
          </div>
          <div className={`${styles.distributionItem} ${styles.distributionClickable}`} onClick={() => handleFilterAndScroll('neutral')}>
            <span className={styles.distributionLabel}>Neutraal</span>
            <div className={styles.distributionBar}>
              <div
                className={styles.distributionFill}
                style={{
                  width: `${(sentimentData.sentimentDistribution.neutral / Math.max(1, sentimentData.messages.length)) * 100}%`,
                  backgroundColor: '#FFC107'
                }}
              />
            </div>
            <span className={styles.distributionValue}>
              {sentimentData.sentimentDistribution.neutral}
            </span>
          </div>
          <div className={`${styles.distributionItem} ${styles.distributionClickable}`} onClick={() => handleFilterAndScroll('negative')}>
            <span className={styles.distributionLabel}>Negatief</span>
            <div className={styles.distributionBar}>
              <div
                className={styles.distributionFill}
                style={{
                  width: `${(sentimentData.sentimentDistribution.negative / Math.max(1, sentimentData.messages.length)) * 100}%`,
                  backgroundColor: '#FF6B35'
                }}
              />
            </div>
            <span className={styles.distributionValue}>
              {sentimentData.sentimentDistribution.negative}
            </span>
          </div>
        </div>
      </div>

      {/* Search Query Info */}
      {sentimentData.searchQuery && (
        <div className={styles.card}>
          <h3>Zoekquery</h3>
          <div className={styles.searchQuery}>
            <code>{sentimentData.searchQuery}</code>
          </div>
          {sentimentData.coostoProjectId && (
            <div className={styles.coostoInfo}>
              Coosto Project ID: {sentimentData.coostoProjectId}
            </div>
          )}
        </div>
      )}

      {/* Periode Vergelijking */}
      <div className={styles.card}>
        <div
          className={styles.periodComparisonHeader}
          onClick={() => setShowPeriodComparison(!showPeriodComparison)}
        >
          <h3>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 8, verticalAlign: 'middle' }}>
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
            Periodevergelijking
          </h3>
          <span className={styles.expandIcon}>{showPeriodComparison ? '−' : '+'}</span>
        </div>
        <p className={styles.periodDescription}>
          Vergelijk het sentiment tussen twee periodes om het effect van communicatie-inzet te evalueren.
        </p>

        {showPeriodComparison && (() => {
          const comparison = getPeriodComparison();
          if (!comparison) return null;

          const renderDist = (label: string, a: { positive: number; negative: number; neutral: number; total: number }, b: { positive: number; negative: number; neutral: number; total: number }) => {
            const pctA = a.total > 0 ? Math.round((a.positive / a.total) * 100) : 0;
            const pctB = b.total > 0 ? Math.round((b.positive / b.total) * 100) : 0;
            const diff = pctB - pctA;
            const negPctA = a.total > 0 ? Math.round((a.negative / a.total) * 100) : 0;
            const negPctB = b.total > 0 ? Math.round((b.negative / b.total) * 100) : 0;
            const negDiff = negPctB - negPctA;

            return (
              <div className={styles.comparisonCategory}>
                <h4>{label}</h4>
                <div className={styles.comparisonGrid}>
                  <div className={styles.comparisonColumn}>
                    <span className={styles.comparisonPeriodLabel}>Periode A</span>
                    <span className={styles.comparisonCount}>{a.total} berichten</span>
                    <div className={styles.comparisonBar}>
                      <div className={styles.comparisonBarPositive} style={{ width: `${a.total > 0 ? (a.positive / a.total) * 100 : 0}%` }} />
                      <div className={styles.comparisonBarNeutral} style={{ width: `${a.total > 0 ? (a.neutral / a.total) * 100 : 0}%` }} />
                      <div className={styles.comparisonBarNegative} style={{ width: `${a.total > 0 ? (a.negative / a.total) * 100 : 0}%` }} />
                    </div>
                    <div className={styles.comparisonNumbers}>
                      <span style={{ color: '#8BC34A' }}>{a.positive} pos</span>
                      <span style={{ color: '#FFC107' }}>{a.neutral} neu</span>
                      <span style={{ color: '#FF6B35' }}>{a.negative} neg</span>
                    </div>
                  </div>
                  <div className={styles.comparisonArrow}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#9DA6A8" strokeWidth="2">
                      <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
                    </svg>
                  </div>
                  <div className={styles.comparisonColumn}>
                    <span className={styles.comparisonPeriodLabel}>Periode B</span>
                    <span className={styles.comparisonCount}>{b.total} berichten</span>
                    <div className={styles.comparisonBar}>
                      <div className={styles.comparisonBarPositive} style={{ width: `${b.total > 0 ? (b.positive / b.total) * 100 : 0}%` }} />
                      <div className={styles.comparisonBarNeutral} style={{ width: `${b.total > 0 ? (b.neutral / b.total) * 100 : 0}%` }} />
                      <div className={styles.comparisonBarNegative} style={{ width: `${b.total > 0 ? (b.negative / b.total) * 100 : 0}%` }} />
                    </div>
                    <div className={styles.comparisonNumbers}>
                      <span style={{ color: '#8BC34A' }}>{b.positive} pos</span>
                      <span style={{ color: '#FFC107' }}>{b.neutral} neu</span>
                      <span style={{ color: '#FF6B35' }}>{b.negative} neg</span>
                    </div>
                  </div>
                  <div className={styles.comparisonDelta}>
                    <div className={styles.deltaItem}>
                      <span className={styles.deltaLabel}>Positief</span>
                      <span className={styles.deltaValue} style={{ color: diff > 0 ? '#4CAF50' : diff < 0 ? '#F44336' : '#9DA6A8' }}>
                        {diff > 0 ? '+' : ''}{diff}%
                      </span>
                    </div>
                    <div className={styles.deltaItem}>
                      <span className={styles.deltaLabel}>Negatief</span>
                      <span className={styles.deltaValue} style={{ color: negDiff < 0 ? '#4CAF50' : negDiff > 0 ? '#F44336' : '#9DA6A8' }}>
                        {negDiff > 0 ? '+' : ''}{negDiff}%
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            );
          };

          return (
            <div className={styles.periodComparisonContent}>
              <div className={styles.periodSelectors}>
                <div className={styles.periodSelector}>
                  <label>Periode A (voor)</label>
                  <div className={styles.periodInputs}>
                    <input type="date" value={periodA.from} onChange={e => setPeriodA({ ...periodA, from: e.target.value })} className={styles.periodInput} />
                    <span>t/m</span>
                    <input type="date" value={periodA.to} onChange={e => setPeriodA({ ...periodA, to: e.target.value })} className={styles.periodInput} />
                  </div>
                </div>
                <div className={styles.periodSelector}>
                  <label>Periode B (na)</label>
                  <div className={styles.periodInputs}>
                    <input type="date" value={periodB.from} onChange={e => setPeriodB({ ...periodB, from: e.target.value })} className={styles.periodInput} />
                    <span>t/m</span>
                    <input type="date" value={periodB.to} onChange={e => setPeriodB({ ...periodB, to: e.target.value })} className={styles.periodInput} />
                  </div>
                </div>
              </div>

              {renderDist('Alle bronnen', comparison.a.all, comparison.b.all)}
              {renderDist('Social media', comparison.a.social, comparison.b.social)}
              {renderDist('Online berichtgeving (media)', comparison.a.media, comparison.b.media)}

              {(comparison.a.all.total === 0 && comparison.b.all.total === 0) && (
                <div className={styles.noComparisonData}>
                  Geen berichten gevonden in de geselecteerde periodes. Pas de datums aan of sync eerst nieuwe data.
                </div>
              )}
            </div>
          );
        })()}
      </div>

      {/* Filter Buttons */}
      <div className={styles.filterSection} ref={messagesListRef}>
        <h3>
          Filter Berichten
          {activeVoiceCategory && (
            <span className={styles.activeVoiceLabel}>
              — gefilterd op stemcategorie
              <button className={styles.clearVoiceFilter} onClick={() => { setActiveVoiceCategory(null); setSelectedFilter('all'); }}>
                Wis filter
              </button>
            </span>
          )}
        </h3>
        <div className={styles.filterButtons}>
          <button
            className={`${styles.filterButton} ${selectedFilter === 'all' ? styles.active : ''}`}
            onClick={() => { setSelectedFilter('all'); setActiveVoiceCategory(null); }}
          >
            Alle ({getCleanedMessages().length})
          </button>
          <button
            className={`${styles.filterButton} ${styles.positive} ${selectedFilter === 'positive' ? styles.active : ''}`}
            onClick={() => { setSelectedFilter('positive'); setActiveVoiceCategory(null); }}
          >
            Positief ({sentimentData.sentimentDistribution.positive})
          </button>
          <button
            className={`${styles.filterButton} ${styles.neutral} ${selectedFilter === 'neutral' ? styles.active : ''}`}
            onClick={() => { setSelectedFilter('neutral'); setActiveVoiceCategory(null); }}
          >
            Neutraal ({sentimentData.sentimentDistribution.neutral})
          </button>
          <button
            className={`${styles.filterButton} ${styles.negative} ${selectedFilter === 'negative' ? styles.active : ''}`}
            onClick={() => { setSelectedFilter('negative'); setActiveVoiceCategory(null); }}
          >
            Negatief ({sentimentData.sentimentDistribution.negative})
          </button>
        </div>

        {/* Bron Filter */}
        <div className={styles.sourceFilterRow}>
          <span className={styles.sourceFilterLabel}>Bron:</span>
          <div className={styles.sourceFilterButtons}>
            {([
              { key: 'all' as SourceCategory, label: 'Alle bronnen' },
              { key: 'media' as SourceCategory, label: 'Media' },
              { key: 'social' as SourceCategory, label: 'Social media' },
              { key: 'publicatie' as SourceCategory, label: 'Publicaties' },
            ]).map(({ key, label }) => {
              const count = key === 'all'
                ? getCleanedMessages().length
                : getCleanedMessages().filter(m => getSourceCategory(m.sourceType, m.url) === key).length;
              return (
                <button
                  key={key}
                  className={`${styles.sourceFilterButton} ${selectedSourceFilter === key ? styles.active : ''}`}
                  onClick={() => setSelectedSourceFilter(key)}
                >
                  {label} ({count})
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Messages List */}
      <div className={styles.sentimentsList}>
        <div className={styles.messagesHeader}>
          <h2>Recente Berichten</h2>
          <div className={styles.messageInfo}>
            Showing {Math.min(filteredMessages.length, (currentPage - 1) * messagesPerPage + 1)}-{Math.min(currentPage * messagesPerPage, filteredMessages.length)} of {filteredMessages.length} messages
          </div>
        </div>

        {/* Pagination Controls Top */}
        {totalPages > 1 && (
          <div className={styles.paginationControls}>
            <button
              className={styles.paginationButton}
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              ← Previous
            </button>
            <div className={styles.paginationInfo}>
              Page {currentPage} of {totalPages}
            </div>
            <button
              className={styles.paginationButton}
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              Next →
            </button>
          </div>
        )}

        <div className={styles.sentimentsGrid}>
          {paginatedMessages.map((item) => {
            const source = getSourceDisplay(item.sourceType, item.url);
            const reliability = getSourceReliability(item.sourceType, item.url, item.author);
            return (
            <div key={item.id} className={styles.sentimentCard}>
              <div className={styles.sentimentHeader}>
                <div className={styles.sourceIndicator}>
                  <span className={styles.sourceIcon}>{source.icon}</span>
                  <span className={styles.sourceType}>{source.label}</span>
                  <span
                    className={styles.reliabilityBadge}
                    style={{ backgroundColor: reliability.color }}
                    title={`Betrouwbaarheid: ${reliability.label}`}
                  >
                    {reliability.level === 3 ? '|||' : reliability.level === 2 ? '||' : '|'}
                  </span>
                </div>
                <div
                  className={styles.sentimentBadge}
                  style={{
                    backgroundColor:
                      item.sentiment === 'positive' ? '#8BC34A' :
                      item.sentiment === 'negative' ? '#FF6B35' :
                      '#FFC107'
                  }}
                >
                  {item.sentiment === 'positive' ? <ThumbsUpIcon size={16} /> :
                   item.sentiment === 'negative' ? <ThumbsDownIcon size={16} /> :
                   <MinusCircleIcon size={16} />}
                </div>
              </div>

              <div className={styles.sentimentContent}>
                <p className={styles.messageText}>{item.text}</p>
              </div>

              {item.author && (
                <div className={styles.authorSection}>
                  {item.author.avatar && (
                    <img
                      src={item.author.avatar}
                      alt={item.author.displayName}
                      className={styles.authorAvatar}
                      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                    />
                  )}
                  <div className={styles.authorInfo}>
                    <span className={styles.authorName}>
                      {item.author.displayName || item.author.userName}
                    </span>
                    {item.author.followersAmount > 0 && (
                      <span className={styles.authorFollowers}>
                        {formatNumber(item.author.followersAmount)} volgers
                      </span>
                    )}
                  </div>
                </div>
              )}

              {item.media && item.media.length > 0 && (
                <div className={styles.mediaSection}>
                  {item.media.map((media, index) => (
                    media.type === 'image' && (
                      <img
                        key={index}
                        src={media.preview?.url || media.url}
                        alt="Media"
                        className={styles.mediaImage}
                        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                      />
                    )
                  ))}
                </div>
              )}

              <div className={styles.messageFooter}>
                <div className={styles.messageStats}>
                  <span className={styles.messageDate}>
                    {new Date(item.timestamp * 1000).toLocaleDateString('nl-NL', {
                      day: 'numeric',
                      month: 'short',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                  <span className={styles.messageReach}>
                    Bereik: {formatNumber(item.reach || 0)}
                  </span>
                </div>
                <div className={styles.messageActions}>
                  {item.isRetweet && <span className={styles.badge}>Retweet</span>}
                  {item.isRepost && <span className={styles.badge}>Repost</span>}
                  <a
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.viewLink}
                  >
                    Bekijk →
                  </a>
                </div>
              </div>
            </div>
            );
          })}
        </div>

        {paginatedMessages.length === 0 && (
          <div className={styles.noMessages}>
            <p>Geen berichten gevonden met het geselecteerde filter.</p>
          </div>
        )}

        {/* Pagination Controls Bottom */}
        {totalPages > 1 && (
          <div className={styles.paginationControls}>
            <button
              className={styles.paginationButton}
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              ← Previous
            </button>
            <div className={styles.paginationPages}>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum: number;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                return (
                  <button
                    key={pageNum}
                    className={`${styles.pageNumber} ${currentPage === pageNum ? styles.activePage : ''}`}
                    onClick={() => setCurrentPage(pageNum)}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            <button
              className={styles.paginationButton}
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              Next →
            </button>
          </div>
        )}
      </div>
        </>
      ) : (
        <div className={styles.empty}>
          <p>Geen sentimentdata beschikbaar voor dit project.</p>
          <Button
            variant="primary"
            onClick={() => setActiveTab('settings')}
          >
            Configureer Coosto Integratie
          </Button>
        </div>
      )}
    </div>
  );
};